import React, { useState, useEffect } from 'react';
import { Button, Table, Modal, Form, Row, Col, Card, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import ReceiptScanner from './ReceiptScanner';

const MaterialsTab = ({ projectId, token }) => {
  const [materials, setMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedMaterialId, setSelectedMaterialId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    materialName: '',
    category: '',
    quantity: '',
    unit: 'each',
    unitCost: '',
    vendor: '',
    purchaseDate: '',
    notes: ''
  });
  
  // Fetch materials for this project
  useEffect(() => {
    fetchMaterials();
    fetchVendors();
    fetchCategories();
  }, [projectId]);
  
  const fetchMaterials = async () => {
    try {
      const response = await fetch(`/api/materials/project/${projectId}`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch materials');
      }
      
      const data = await response.json();
      setMaterials(data);
    } catch (err) {
      console.error('Error fetching materials:', err);
      toast.error('Failed to load materials');
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchVendors = async () => {
    try {
      const response = await fetch('/api/materials/vendors', {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendors(data);
      }
    } catch (err) {
      console.error('Error fetching vendors:', err);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/materials/categories', {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  const resetForm = () => {
    setFormData({
      materialName: '',
      category: '',
      quantity: '',
      unit: 'each',
      unitCost: '',
      vendor: '',
      purchaseDate: '',
      notes: ''
    });
    setIsEditing(false);
    setSelectedMaterialId(null);
  };
  
  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };
  
  const openEditModal = (material) => {
    setFormData({
      materialName: material.MaterialName,
      category: material.Category || '',
      quantity: Math.abs(material.Quantity).toString(),
      unit: material.Unit,
      unitCost: Math.abs(material.UnitCost).toString(),
      vendor: material.Vendor || '',
      purchaseDate: material.PurchaseDate ? new Date(material.PurchaseDate).toISOString().split('T')[0] : '',
      notes: material.Notes || ''
    });
    setSelectedMaterialId(material.MaterialId);
    setIsEditing(true);
    setShowModal(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const { materialName, quantity, unitCost } = formData;
    
    if (!materialName || !quantity || !unitCost) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const url = isEditing 
        ? `/api/materials/${selectedMaterialId}`
        : '/api/materials';
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const body = isEditing
        ? formData
        : { ...formData, projectId };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} material`);
      }
      
      toast.success(`Material ${isEditing ? 'updated' : 'added'} successfully`);
      setShowModal(false);
      resetForm();
      fetchMaterials();
      fetchVendors();
      fetchCategories();
    } catch (err) {
      console.error('Error saving material:', err);
      toast.error(`Failed to ${isEditing ? 'update' : 'add'} material`);
    }
  };
  
  const handleDelete = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this material?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/materials/${materialId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete material');
      }
      
      toast.success('Material deleted successfully');
      fetchMaterials();
    } catch (err) {
      console.error('Error deleting material:', err);
      toast.error('Failed to delete material');
    }
  };
  
  // Format currency - shows returns in red with parentheses
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (num < 0) {
      return <span className="text-danger">({'$' + Math.abs(num).toFixed(2)})</span>;
    }
    return '$' + num.toFixed(2);
  };
  
  // Calculate totals
  const totalMaterialsCost = materials.reduce((sum, m) => sum + parseFloat(m.TotalCost || 0), 0);
  
  // Calculate total cost in form
  const formTotalCost = formData.quantity && formData.unitCost
    ? (parseFloat(formData.quantity) * parseFloat(formData.unitCost)).toFixed(2)
    : '0.00';
  
  if (isLoading) {
    return <div className="text-center py-4">Loading materials...</div>;
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Materials</h5>
        <div>
          <Button variant="primary" size="sm" onClick={openAddModal}>
            <FaPlus className="me-1" />
            Add Material
          </Button>
          <ReceiptScanner 
            projectId={projectId} 
            token={token} 
            onMaterialsAdded={fetchMaterials}
          />
        </div>
      </div>
      
      {materials.length > 0 && (
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="text-center">
                  <h6 className="text-muted">Total Items</h6>
                  <h3>{materials.length}</h3>
                </div>
              </Col>
              <Col md={6}>
                <div className="text-center">
                  <h6 className="text-muted">Net Cost</h6>
                  <h3 className={totalMaterialsCost >= 0 ? 'text-success' : 'text-danger'}>
                    {totalMaterialsCost >= 0 
                      ? '$' + totalMaterialsCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '($' + Math.abs(totalMaterialsCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ')'
                    }
                  </h3>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      
      {materials.length > 0 ? (
        <div className="table-responsive">
          <Table striped hover>
            <thead>
              <tr>
                <th>Material</th>
                <th>Category</th>
                <th>Quantity</th>
                <th>Unit Cost</th>
                <th>Total Cost</th>
                <th>Vendor</th>
                <th>Purchase Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {materials.map(material => (
                <tr key={material.MaterialId}>
                  <td>
                    <div className="fw-bold">{material.MaterialName}</div>
                    {material.Notes && (
                      <small className={material.Notes.startsWith('RETURN') ? 'text-danger' : 'text-muted'}>
                        {material.Notes}
                      </small>
                    )}
                  </td>
                  <td>
                    {material.Category && (
                      <span className="badge bg-secondary">{material.Category}</span>
                    )}
                  </td>
                  <td>{parseFloat(material.Quantity).toFixed(2)} {material.Unit}</td>
                  <td>{formatCurrency(material.UnitCost)}</td>
                  <td className="fw-bold">{formatCurrency(material.TotalCost)}</td>
                  <td>{material.Vendor || '-'}</td>
                  <td>
                    {material.PurchaseDate 
                      ? new Date(material.PurchaseDate).toLocaleDateString()
                      : '-'
                    }
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1"
                      onClick={() => openEditModal(material)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(material.MaterialId)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <Alert variant="info" className="text-center">
          <p className="mb-3">No materials added to this project yet.</p>
          <Button variant="primary" onClick={openAddModal}>
            <FaPlus className="me-1" />
            Add Your First Material
          </Button>
        </Alert>
      )}
      
      {/* Add/Edit Material Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Material' : 'Add Material'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Material Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="materialName"
                    value={formData.materialName}
                    onChange={handleFormChange}
                    placeholder="e.g., Ceramic Tile, Bathtub, Paint"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Control
                    as="select"
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                  >
                    <option value="">Select category...</option>
                    <option value="Plumbing">Plumbing</option>
                    <option value="Electrical">Electrical</option>
                    <option value="Flooring">Flooring</option>
                    <option value="Fixtures">Fixtures</option>
                    <option value="Lumber">Lumber</option>
                    <option value="Paint">Paint</option>
                    <option value="Hardware">Hardware</option>
                    <option value="HVAC">HVAC</option>
                    <option value="Roofing">Roofing</option>
                    <option value="Other">Other</option>
                    {categories
                      .filter(c => !['Plumbing', 'Electrical', 'Flooring', 'Fixtures', 'Lumber', 'Paint', 'Hardware', 'HVAC', 'Roofing', 'Other'].includes(c))
                      .map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))
                    }
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantity <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleFormChange}
                    placeholder="e.g., 150"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit</Form.Label>
                  <Form.Control
                    as="select"
                    name="unit"
                    value={formData.unit}
                    onChange={handleFormChange}
                  >
                    <option value="each">Each</option>
                    <option value="sq ft">Square Feet</option>
                    <option value="linear ft">Linear Feet</option>
                    <option value="gallon">Gallon</option>
                    <option value="box">Box</option>
                    <option value="bag">Bag</option>
                    <option value="sheet">Sheet</option>
                    <option value="roll">Roll</option>
                    <option value="set">Set</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit Cost ($) <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="unitCost"
                    value={formData.unitCost}
                    onChange={handleFormChange}
                    placeholder="e.g., 4.50"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            {formData.quantity && formData.unitCost && (
              <Alert variant="info">
                <strong>Total Cost:</strong> ${formTotalCost}
              </Alert>
            )}
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vendor</Form.Label>
                  <Form.Control
                    type="text"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleFormChange}
                    placeholder="e.g., Home Depot, Lowe's"
                    list="vendor-datalist"
                  />
                  <datalist id="vendor-datalist">
                    {vendors.map((vendor, idx) => (
                      <option key={idx} value={vendor} />
                    ))}
                  </datalist>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Purchase Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="purchaseDate"
                    value={formData.purchaseDate}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
                placeholder="Any additional notes about this material..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {isEditing ? 'Update Material' : 'Add Material'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default MaterialsTab;
